import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowRight } from 'lucide-react';
import type { ReadPass, ReadStep, SiteRead } from '../services/api';
import { peekDocument, peekDocumentStream, peekSite, peekSiteStream, publicStarterDeck, publicToolSearch, signupWithDeck } from '../services/api';
import { clearAuthState } from '../services/authStorage';
import { OrbitDeck } from '../components/orbit-deck/OrbitDeck';
import type { DeckAnswer, DeckCardModel, DeckLearned, DeckOption, DeckRailState, DeckWork, DeckWorkStep } from '../components/orbit-deck/types';
import { fillPasses, mergePass, READ_PASSES, stopWork } from '../components/orbit-deck/readPasses';
import { hostOf, siteRoot } from '../lib/site';
import { AuthShell, FIELD, FOOTNOTE, GoogleMark, LABEL, OrDivider, PasswordField, PRIMARY, QUIET } from './auth/AuthShell';

/*
 * Sign-up is the deck. The starter cards the platform set, dealt one at a
 * time; a website card first so Orbit can read while the guest flips; the
 * identity card last, once there is something worth sending. Answers,
 * confirmed facts and skips travel with the signup and land in the new
 * company's dossier. Nothing is stored before the account exists.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface StarterCard { key: string; section: string; label: string; kind: string; options: { value: string; label: string }[]; why: string; placeholder: string; rank: number; }

const inputFor = (kind: string): DeckCardModel['input'] => {
  if (kind === 'select') return 'choose';
  if (kind === 'multiselect') return 'choose_many';
  if (kind === 'number') return 'number';
  if (kind === 'text' || kind === 'url') return 'text';
  return 'say';
};

const QUESTION_STARTS = new Set(['what', 'which', 'who', 'where', 'when', 'how', 'why', 'do', 'does', 'is', 'are', 'can', 'any', 'will', 'would', 'should']);
const IMPERATIVE_STARTS = new Set(['describe', 'tell', 'name', 'list', 'pick', 'choose', 'rate', 'explain', 'share', 'give', 'say']);

const questionFor = (label: string, kind: string): string => {
  const text = label.trim().replace(/\.$/, '');
  if (!text) return 'Tell Orbit about this.';
  if (text.endsWith('?')) return text;
  const first = text.split(' ')[0].toLowerCase();
  if (QUESTION_STARTS.has(first)) return `${text}?`;
  if (IMPERATIVE_STARTS.has(first)) return `${text}.`;
  const lowered = text.length > 1 && text[1] === text[1].toUpperCase() && text[1] !== text[1].toLowerCase() ? text : text[0].toLowerCase() + text.slice(1);
  if (kind === 'select' || kind === 'multiselect') return `Which applies to your ${lowered}?`;
  return `What is the ${lowered}?`;
};

const WEBSITE_CARD: DeckCardModel = {
  id: 'a.website', fieldKey: 'a.website', tag: 'ask', tagLabel: 'Start here', title: 'Company or website',
  why: 'One website is enough to start. Orbit reads it while you flip through.', input: 'text', placeholder: 'yourcompany.com, or the company name',
};

const DOCUMENTS_CARD: DeckCardModel = {
  id: 'documents', tag: 'evidence', tagLabel: 'Or hand it over', title: 'Have anything Orbit should read?',
  why: 'An org chart, an SOP or a tools export answers more than any card. The cards it answers leave the deck.', input: 'evidence',
};

const looksLikeWebsite = (text: string): boolean => /^(https?:\/\/)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/.*)?$/i.test(text.trim());

const searchTools = async (q: string): Promise<DeckOption[]> => {
  const res = await publicToolSearch(q);
  return res.tools.map((t) => ({ value: t.slug, label: t.label, logo: t.logo, source: t.source }));
};

const IDENTITY_CARD: DeckCardModel = {
  id: 'identity', tag: 'confirm', tagLabel: 'Last, so this reaches you', title: 'Where should Orbit send the first read?',
  why: 'The full read lands in your Room in a few minutes.', input: 'identity', noSkip: true,
};

const AREA_OF: Record<string, string> = { a: 'company', b: 'stack', c: 'org', d: 'goals', e: 'problem' };

/* The read owns the first card for the whole sequence: a prelude, the seven
   passes, and a close. DeckRead paces that and says when it is over. This is
   only the backstop for a panel that never reports at all, so it sits well
   past the sequence's own budget rather than cutting it short. */
const HANDOFF_CAP = 45000;
const LANDED = ['done', 'empty', 'failed'];
const AREA_LABEL: Record<string, string> = { goals: 'What should get better', company: 'What the business does', org: 'Departments and people', stack: 'Tools in use' };

/*
 * The earlier sign-up: the deck itself, with the identity card dealt last.
 * Kept whole and exported so it can come back with one line in App.tsx.
 * /signup now renders the form below: the account first, then the deck.
 */
export const SignupDeck: React.FC = () => {
  const [params] = useSearchParams();
  const referral = params.get('ref');
  const [starter, setStarter] = useState<StarterCard[] | null>(null);
  const [partner, setPartner] = useState<{ id: string; name: string } | null>(null);
  const [provider, setProvider] = useState<{ id: string; name: string } | null>(null);
  const [referralError, setReferralError] = useState('');
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [peeked, setPeeked] = useState<{ key: string; value: unknown; confidence: number; label: string; value_display: string }[]>([]);
  const [confirmed, setConfirmed] = useState<string[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [website, setWebsite] = useState('');
  const [learned, setLearned] = useState<DeckLearned[]>([{ text: 'Nothing shared yet. You approve what Orbit keeps.' }]);
  const [auto, setAuto] = useState(0);
  const [removed, setRemoved] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [work, setWork] = useState<DeckWork | null>(null);

  const [pagesRead, setPagesRead] = useState<number | undefined>(undefined);
  // Fields per pass, keyed rather than summed as they arrive: a pass reports
  // twice, and an accumulator would count a re-render as a second pass.
  const [factsByPass, setFactsByPass] = useState<Record<string, number>>({});

  /*
   * The read owns the first card until every pass has had its moment on
   * screen, and the deck paces that walk: no pass flashes past, none stalls
   * it, and a pass still reading when its turn comes gets a second one once
   * its answer is in. Whatever is still running when the walk ends carries on
   * beside the deck, which is what the rail has always claimed. The confirm
   * card is dealt the moment the read is in, ahead of the questions it exists
   * to remove.
   */
  const handOff = useRef<(() => void) | null>(null);
  // Did the stream narrate at all? A browser that would not stream falls back
  // to the plain read, and then there is no walk to wait on.
  const narrated = useRef(false);

  /* The deck has shown every pass it is going to show. */
  const readWalkDone = () => {
    const go = handOff.current;
    handOff.current = null;
    if (go) go();
  };
  const [reading, setReading] = useState(false);
  const [handedOff, setHandedOff] = useState(false);
  const [lateCards, setLateCards] = useState<DeckCardModel[]>([]);

  // One line per step the server says it has taken. A step that reports again
  // under the same key replaces its own line, so a long read stays one list.
  const noteStep = (step: ReadStep) => setWork((prev) => {
    if (!prev) return prev;
    const steps: DeckWorkStep[] = [...prev.steps];
    const at = steps.map((s) => s.key).lastIndexOf(step.key);
    if (at >= 0 && steps[at].state === 'doing') steps[at] = step;
    else steps.push(step);
    return { ...prev, steps };
  });

  // The seven passes. Each reports twice, starting and landing, and the report
  // is folded into the row with its key: data about a pass, never a cue about
  // which pass the walk should be showing.
  const notePass = (pass: ReadPass) => {
    narrated.current = true;
    // Both counters only ever climb. A pass reports twice, and the first
    // report carries the totals as they were when it STARTED, so taking the
    // number at face value made the rail count backwards mid-read.
    setPagesRead((n) => Math.max(n || 0, pass.pages_read));
    if (pass.found > 0) setFactsByPass((m) => ({ ...m, [pass.key]: pass.found }));
    setWork((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        passes: mergePass(prev.passes || [], pass),
        pagesRead: Math.max(prev.pagesRead || 0, pass.pages_read),
      };
    });
  };

  useEffect(() => {
    publicStarterDeck(referral).then((d) => {
      setStarter(d.cards);
      setPartner(d.partner || null);
      setProvider(d.provider || null);
      setReferralError(d.referral_error || '');
    }).catch(() => setStarter([]));
  }, [referral]);

  const cards = useMemo<DeckCardModel[]>(() => {
    if (starter === null) return [];
    const fromStarter = starter.filter((c) => c.key !== 'a.website').map((c): DeckCardModel => ({
      id: c.key, fieldKey: c.key, tag: 'ask', tagLabel: `Card ${c.rank} of ${starter.length}`, title: questionFor(c.label, c.kind),
      why: c.why || 'Plain words are enough. Orbit turns them into things to verify, not conclusions.', input: inputFor(c.kind),
      options: c.options || [], placeholder: c.placeholder || '', search: c.key === 'b.tools_used' ? 'tools' : undefined,
    }));
    return [WEBSITE_CARD, ...lateCards, ...fromStarter, DOCUMENTS_CARD, IDENTITY_CARD];
  }, [starter, lateCards]);

  const factsRead = Object.values(factsByPass).reduce((n, v) => n + v, 0);

  /* Only once the deck has moved on: while the read still owns the card, the
     card is already showing it and the rail would say it twice. */
  const readProgress = useMemo(() => {
    if (!reading || !handedOff || !work || !work.passes) return null;
    const passes = work.passes;
    const done = passes.filter((p) => LANDED.includes(p.state)).length;
    const doing = passes.find((p) => !LANDED.includes(p.state));
    let label = 'Finishing the read';
    if (doing) label = doing.title;
    return { done, total: passes.length, doing: label };
  }, [reading, handedOff, work]);

  const rail = useMemo<DeckRailState>(() => {
    const keys = new Set([...Object.keys(answers), ...confirmed]);
    const totals: Record<string, { have: number; all: number }> = { goals: { have: 0, all: 1 }, company: { have: 0, all: 1 }, org: { have: 0, all: 1 }, stack: { have: 0, all: 1 } };
    cards.forEach((c) => {
      if (!c.fieldKey) return;
      const area = AREA_OF[c.fieldKey.split('.')[0]] || 'company';
      if (!totals[area]) return;
      totals[area].all += 1;
      if (keys.has(c.fieldKey)) totals[area].have += 1;
    });
    peeked.forEach((p) => { const area = AREA_OF[p.key.split('.')[0]] || 'company'; if (totals[area]) totals[area].have += 0.5; });
    const left = cards.filter((c) => c.fieldKey && !(c.fieldKey in answers) && !skipped.includes(c.fieldKey) && !removed.includes(c.id) && !confirmed.includes(c.fieldKey)).length;
    const departments = peeked.find((p) => p.key === 'c.departments');
    let found = 0;
    if (departments && Array.isArray(departments.value)) found = departments.value.length;
    return {
      site: website,
      pages: pagesRead,
      facts: Math.max(peeked.length + Object.keys(answers).length, factsRead),
      auto,
      skipped: skipped.length,
      left,
      departments: found,
      areas: Object.entries(totals).map(([key, t]) => ({ key, label: AREA_LABEL[key] || key, pct: Math.min(100, Math.round((t.have / Math.max(t.all, 1)) * 100)) })),
      read: readProgress,
      learned,
      doors: [],
    };
  }, [answers, confirmed, peeked, website, auto, learned, cards, skipped, removed, pagesRead, factsRead, readProgress]);

  const finish = async (identity: { name: string; email: string; password: string }) => {
    setSubmitting(true);
    let linkTo = referral;
    if (referralError) linkTo = null;
    try {
      const response = await signupWithDeck({
        name: identity.name.trim(), email: identity.email.trim(), password: identity.password, referral: linkTo,
        deck: { answers, peeked: peeked.map((p) => ({ key: p.key, value: p.value, confidence: p.confidence })), confirmed, skipped, website },
      });
      if (!response.token || !response.user) {
        toast.error('Unexpected signup response');
        return;
      }
      clearAuthState();
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user_role', response.user.role);
      localStorage.setItem('user_id', response.user.id);
      if (response.user.client_id) localStorage.setItem('client_id', response.user.client_id);
      if (response.user.email) localStorage.setItem('user_email', response.user.email);
      if (response.user.name) localStorage.setItem('user_name', response.user.name);
      toast.success('Account created. Orbit is reading your site.');
      window.location.href = '/orbit/deck';
    } catch (error) {
      const err = error as { response?: { status?: number; data?: { detail?: string } } };
      let message = err.response?.data?.detail || 'Could not create account';
      if (err.response?.status === 409 && !err.response?.data?.detail) {
        message = 'An account with this email already exists';
      }
      toast.error(message);
      throw new Error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // The narrated read is the same work as the plain one. A browser or proxy
  // that will not stream still gets the answer, just without the lines.
  const readSite = async (site: string) => {
    try {
      return await peekSiteStream(site, noteStep, notePass);
    } catch {
      return peekSite(site);
    }
  };
  const readDoc = async (file: File) => {
    try {
      return await peekDocumentStream(file, noteStep);
    } catch {
      return peekDocument(file);
    }
  };

  const onAnswer = async (card: DeckCardModel, answer: DeckAnswer): Promise<DeckCardModel[] | void> => {
    if (card.input === 'identity') {
      if (answer.identity) await finish(answer.identity);
      return;
    }
    if (card.input === 'confirm') {
      const ok = (answer.confirm || []).filter((d) => d.action !== 'skip');
      setConfirmed((c) => [...c, ...ok.filter((d) => d.action === 'confirm').map((d) => d.field_key)]);
      const corrections: Record<string, unknown> = {};
      ok.filter((d) => d.action === 'correct' && d.value !== undefined && d.value !== '').forEach((d) => { corrections[d.field_key] = d.value; });
      if (Object.keys(corrections).length) setAnswers((a) => ({ ...a, ...corrections }));
      setLearned((l) => [{ text: `Company shape confirmed. ${ok.length} questions removed from the deck.`, gold: true }, ...l].slice(0, 6));
      return;
    }
    if (card.id === 'a.website') {
      const typed = String(answer.value || '').trim();
      if (!looksLikeWebsite(typed)) {
        setAnswers((a) => ({ ...a, 'a.company_name': typed }));
        setLearned((l) => [{ text: `${typed}: recorded as the company name. Drop a document or add the website later and Orbit reads it.` }, ...l].slice(0, 6));
        return;
      }
      // The company's site, not the campaign page they happened to be on: the
      // map and every pass after it start from the root.
      const site = siteRoot(typed);
      setWebsite(site);
      setAnswers((a) => ({ ...a, 'a.website': site }));
      setLearned((l) => [{ text: `Reading ${hostOf(site)}. Facts appear here as they land.` }, ...l].slice(0, 6));
      setReading(true);
      setWork({
        title: `Reading ${hostOf(site)}`,
        cardId: 'a.website',
        note: 'Seven passes over your site and the open web. One card each, as they land.',
        steps: [],
        site,
        pagesRead: 0,
        passes: READ_PASSES.map((p) => ({ ...p, state: 'waiting', pages: 0, found: 0, line: '' })),
      });
      narrated.current = false;
      const handed = new Promise<void>((resolve) => { handOff.current = resolve; });
      // Not awaited, and the read finishing does not end the sequence: a
      // cached site can come back in eight seconds, and cutting the cards off
      // there is how a person ends up seeing one pass of seven. The sequence
      // plays out and the deck resumes when it says so.
      readSite(site)
        .then((peek) => {
          landed(site, peek);
          // A browser that would not stream still gets the read, just in one
          // piece at the end. Its per-pass rows go in as data, so the passes
          // the sequence has not reached yet still show what they found.
          if (!narrated.current) setWork((prev) => fillPasses(prev, peek));
        })
        .catch(() => {
          setLearned((l) => [{ text: 'Could not reach that site now. Orbit reads it after signup.' }, ...l].slice(0, 6));
          // Not a teardown: the panel closes through its own last card saying
          // what happened, which is the only way past it that looks deliberate.
          setWork((prev) => stopWork(prev, `Orbit could not finish reading ${hostOf(site)}. It reads it again once your account exists.`));
        })
        .finally(() => setReading(false));
      await Promise.race([
        handed,
        new Promise<void>((resolve) => { window.setTimeout(resolve, HANDOFF_CAP); }),
      ]);
      setHandedOff(true);
      return;
    }
    if (card.fieldKey) {
      setAnswers((a) => ({ ...a, [card.fieldKey as string]: answer.value }));
      setLearned((l) => [{ text: `${card.title.replace(/\?$/, '')}: recorded with you as the source.` }, ...l].slice(0, 6));
    }
  };

  /* The read is in. What it answered leaves the deck, and what it read is
     dealt as one confirm card ahead of the questions it just made redundant. */
  const landed = (site: string, peek: SiteRead) => {
    if (!peek.read || peek.facts.length === 0) {
      setLearned((l) => [{ text: `${hostOf(site)} gave Orbit nothing to read yet. It will try again once your account exists.` }, ...l].slice(0, 6));
      return;
    }
    setPeeked(peek.facts);
    const answered = new Set(peek.facts.map((f) => f.key));
    const gone = (starter || []).map((c) => c.key).filter((key) => key !== 'a.website' && answered.has(key));
    setRemoved((r) => [...r, ...gone]);
    setAuto((n) => n + gone.length);
    const news = peek.articles || [];
    const readLine = [
      `${hostOf(site)} read: ${peek.facts_total} facts kept from ${peek.pages_read || 0} pages`,
      news.length > 0 ? `${news.length} article${news.length === 1 ? '' : 's'} about you on file` : '',
      gone.length > 0 ? `${gone.length} card${gone.length === 1 ? '' : 's'} answered for you` : '',
    ].filter(Boolean).join('. ');
    setLearned((l) => [{ text: `${readLine}.`, gold: true }, ...l].slice(0, 6));
    setLateCards([{
      id: 'confirm_peek', tag: 'confirm', tagLabel: 'We read your site', title: 'Did we get this right?', why: 'One tap saves the questions below.', input: 'confirm',
      source: site, confidence: 0.85,
      facts: peek.facts.map((f) => ({ field_key: f.key, label: f.label, value: f.value, value_display: f.value_display, source: `read from ${site}`, confidence: f.confidence })),
    }]);
  };

  const onSkip = (card: DeckCardModel) => {
    if (card.fieldKey) setSkipped((s) => [...s, card.fieldKey as string]);
    if (card.id === 'documents') setSkipped((s) => [...s, 'documents']);
    setLearned((l) => [{ text: `Skipped. Orbit queued "${card.title.replace(/\?$/, '')}" for your first read.` }, ...l].slice(0, 6));
  };

  const onDocument = async (_card: DeckCardModel, file: File): Promise<{ removed?: string[]; insert?: DeckCardModel[] } | void> => {
    setLearned((l) => [{ text: `Reading ${file.name}. Facts appear here as they land.` }, ...l].slice(0, 6));
    setWork({ title: `Reading ${file.name}`, note: 'Orbit pulls the text out, reads it, and keeps only what it can place in your dossier.', steps: [] });
    try {
      const read = await readDoc(file);
      if (!read.read || read.facts.length === 0) {
        setLearned((l) => [{ text: `${file.name} gave Orbit nothing it could place yet. It is read again once your account exists.` }, ...l].slice(0, 6));
        return undefined;
      }
      const already = new Set(peeked.map((p) => p.key));
      const fresh = read.facts.filter((f) => !already.has(f.key));
      setPeeked((p) => [...p, ...fresh]);
      const answeredKeys = read.facts.map((f) => f.key);
      const gone = cards.filter((c) => c.fieldKey && answeredKeys.includes(c.fieldKey) && c.id !== 'a.website').map((c) => c.id);
      setRemoved((r) => [...r, ...gone]);
      setAuto((n) => n + gone.length);
      setLearned((l) => [{ text: `${file.name} read: ${read.facts_total} facts kept.${gone.length ? ` ${gone.length} card${gone.length === 1 ? '' : 's'} answered for you.` : ''}`, gold: true }, ...l].slice(0, 6));
      return {
        removed: gone,
        insert: [{
          id: `confirm_doc_${Date.now()}`, tag: 'confirm', tagLabel: 'We read your document', title: 'Did we get this right?', why: 'One tap saves the questions below.', input: 'confirm',
          source: file.name, confidence: 0.8,
          facts: read.facts.map((f) => ({ field_key: f.key, label: f.label, value: f.value, value_display: f.value_display, source: `read from ${file.name}`, confidence: f.confidence })),
        }],
      };
    } catch {
      setLearned((l) => [{ text: `Could not read ${file.name} now. Drop it again once your account exists.` }, ...l].slice(0, 6));
      return undefined;
    } finally {
      setWork(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f7f5]">
      <header className="flex items-center border-b border-[#e7e7e4] bg-white/90 px-6 py-4">
        <img src="/logo-black.png" alt="AgentsAnywhere" className="h-7 w-auto object-contain" />
      </header>
      <main className="mx-auto max-w-[1200px] px-6 pb-20 pt-10">
        <div className="mb-6">
          <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#777982]">{partner ? `Your Solution Partner: ${partner.name}` : 'The Orbit deck · 90 seconds'}</p>
          <h1 className="m-0 text-[30px] font-bold leading-[1.1] tracking-[-0.02em] text-[#18191d] sm:text-[34px]">Answer what you know.<br /><span className="text-[#92949d]">Skip the rest.</span></h1>
          <p className="mt-3 text-[14px] text-[#666971]">A few cards, not a form. One website is enough to start: Orbit reads it while you flip through, and every card you skip it tries to answer for you. We ask who you are last.</p>
          {partner && <p className="mt-2 text-[13px] text-[#666971]">{partner.name} shared this link. Your company is set up under them, and these are the cards they chose to ask first.</p>}
          {provider && <p className="mt-2 text-[13px] text-[#666971]">{provider.name} shared this link. Your company is set up as their client.</p>}
          {referralError && <p className="mt-3 inline-block rounded-md border border-[#e8b4b4] bg-[#fdf3f3] px-3 py-2 text-[13px] text-[#8a2b2b]">{referralError} You can still set up a company, but it will not be linked to them.</p>}
        </div>
        {starter === null && <p className="text-[13px] text-[#666971]">Dealing the cards</p>}
        {starter !== null && (
          <OrbitDeck
            cards={cards}
            rail={rail}
            removedIds={removed}
            onAnswer={onAnswer}
            onSkip={onSkip}
            onDocument={onDocument}
            searchTools={searchTools}
            work={handedOff ? null : work}
            onReadDone={readWalkDone}
            labels={{ primary: 'Next' }}
          />
        )}
        <div className="mt-8 flex flex-wrap items-center gap-3 text-[13px] text-[#666971]">
          <span>Prefer Google?</span>
          <button type="button" onClick={() => { window.location.href = `${API_BASE_URL}/api/auth/google`; }} className="rounded-full border border-[#dedede] bg-white px-4 py-2 font-semibold text-[#18191d]" disabled={submitting}>Continue with Google</button>
          <span>Your cards are kept for the first read either way.</span>
        </div>
        <p className="mt-4 text-[13px] text-[#666971]">Already have an account? <Link to="/login" className="font-semibold text-[#18191d] underline underline-offset-4">Sign in</Link>.</p>
      </main>
    </div>
  );
};

/*
 * Sign-up is one form on the split screen it shares with sign in: name,
 * email and password, then the company.
 * The account exists the moment it is sent, and the setup follows at /setup.
 * A website is optional here; given one, Orbit starts reading it.
 */
const looksLikeEmail = (text: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.trim());

const Signup: React.FC = () => {
  const [params] = useSearchParams();
  const referral = params.get('ref');
  const [partner, setPartner] = useState<{ id: string; name: string } | null>(null);
  const [provider, setProvider] = useState<{ id: string; name: string } | null>(null);
  const [referralError, setReferralError] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!referral) return;
    publicStarterDeck(referral).then((d) => {
      setPartner(d.partner || null);
      setProvider(d.provider || null);
      setReferralError(d.referral_error || '');
    }).catch(() => undefined);
  }, [referral]);

  const valid = name.trim().length > 0 && looksLikeEmail(email) && password.length >= 8 && company.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError('');
    let linkTo = referral;
    if (referralError) linkTo = null;
    const answers: Record<string, unknown> = { 'a.company_name': company.trim() };
    let site = '';
    if (website.trim() && looksLikeWebsite(website)) {
      site = siteRoot(website.trim());
      answers['a.website'] = site;
    }
    try {
      const response = await signupWithDeck({
        name: name.trim(), email: email.trim(), password, phone: phone.trim(), referral: linkTo,
        deck: { answers, peeked: [], confirmed: [], skipped: [], website: site },
      });
      if (!response.token || !response.user) {
        setError('That did not create the account. Try again.');
        return;
      }
      clearAuthState();
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user_role', response.user.role);
      localStorage.setItem('user_id', response.user.id);
      if (response.user.client_id) localStorage.setItem('client_id', response.user.client_id);
      if (response.user.email) localStorage.setItem('user_email', response.user.email);
      if (response.user.name) localStorage.setItem('user_name', response.user.name);
      toast.success('Account created.');
      window.location.href = '/setup';
    } catch (err) {
      const failure = err as { response?: { status?: number; data?: { detail?: string } } };
      let message = failure.response?.data?.detail || 'Could not create the account. Try again.';
      if (failure.response?.status === 409 && !failure.response?.data?.detail) message = 'An account with this email already exists.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  let submitLabel = 'Continue';
  if (submitting) submitLabel = 'Creating your account';

  /* Google fills in their name and email; the company is the one thing it
     cannot know, and the partner's link rides along so the company is set up
     under them. */
  const continueWithGoogle = () => {
    if (!company.trim()) {
      setError('Add your company name first, then continue with Google.');
      document.getElementById('signup-company')?.focus();
      return;
    }
    const query = new URLSearchParams({ company: company.trim() });
    if (referral && !referralError) query.set('ref', referral);
    window.location.href = `${API_BASE_URL}/api/auth/google?${query.toString()}`;
  };

  return (
    <AuthShell
      tab="signup"
      title="Create your account"
      sub="Your name, email and company. A short setup about you and your apps comes right after."
    >
      {partner && <p className="m-0 mb-5 text-[13.5px] text-[#666971]">Your Solution Partner: <b className="text-[#18191d]">{partner.name}</b>. Your company is set up under them.</p>}
      {provider && <p className="m-0 mb-5 text-[13.5px] text-[#666971]">Your provider: <b className="text-[#18191d]">{provider.name}</b>. Your company is set up as their client.</p>}
      {referralError && <p className="m-0 mb-5 rounded-md border border-[#e8b4b4] bg-[#fdf3f3] px-3 py-2 text-[13px] text-[#8a2b2b]">{referralError} You can still set up a company, but it will not be linked to them.</p>}
      <button type="button" onClick={continueWithGoogle} disabled={submitting} data-testid="signup-google" className={QUIET}>
        <GoogleMark />
        Continue with Google
      </button>
      <p className="m-0 mt-2 text-center text-[12.5px] text-[#92949d]">Add your company name below first. Google fills in your name and email.</p>
      <OrDivider />
      <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="signup-name" className={LABEL}>Name</label>
            <input id="signup-name" data-testid="signup-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" required className={FIELD} />
          </div>
          <div>
            <label htmlFor="signup-phone" className={LABEL}>Phone <span className="font-normal text-[#92949d]">(optional)</span></label>
            <input id="signup-phone" data-testid="signup-phone" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile" className={FIELD} />
          </div>
        </div>
        <div>
          <label htmlFor="signup-email" className={LABEL}>Work email</label>
          <input id="signup-email" data-testid="signup-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required className={FIELD} />
        </div>
        <PasswordField
          id="signup-password"
          testId="signup-password"
          label="Password"
          value={password}
          onChange={setPassword}
          placeholder="8 characters or more"
          autoComplete="new-password"
        />
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="signup-company" className={LABEL}>Company name</label>
            <input id="signup-company" data-testid="signup-company" autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Your company" required className={FIELD} />
          </div>
          <div>
            <label htmlFor="signup-website" className={LABEL}>Website <span className="font-normal text-[#92949d]">(optional)</span></label>
            <input id="signup-website" data-testid="signup-website" autoComplete="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="yourcompany.com" className={FIELD} />
          </div>
        </div>
        {error && <p role="alert" className="m-0 rounded-md border border-[#e8b4b4] bg-[#fdf3f3] px-3 py-2 text-[13px] text-[#8a2b2b]">{error}</p>}
        <button type="submit" disabled={submitting || !valid} className={`${PRIMARY} mt-1 inline-flex items-center justify-center gap-2`} data-testid="signup-submit">
          {submitLabel} <ArrowRight size={18} />
        </button>
      </form>
      <p className={FOOTNOTE}>Invited by a colleague? Use the link in their email instead.</p>
    </AuthShell>
  );
};

export default Signup;
